import type { RouteObject } from 'react-router';
import { Outlet, ScrollRestoration } from 'react-router';
import { DashboardPage } from '@/pages/dashboard-page';
import { LoginPage } from '@/pages/login-page';
import { NotFoundContent } from '@/pages/not-found-page';
import { RegisterPage } from '@/pages/register-page';
import { RouteError } from '@/pages/route-error';
import { AppShell } from './app-shell';
import { FullPageLoader, NOT_FOUND_ROUTE, PublicOnly, RequireAuth } from './route-guards';

/** Resets the scroll position on new locations and restores it on back and forward navigation. */
function RootLayout() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

/**
 * Every unknown URL matches the splat route inside the signed-in layout. RequireAuth renders the standalone
 * not-found page for it when nobody is signed in, instead of redirecting to /login.
 */
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullPageLoader />,
    children: [
      {
        path: '/login',
        element: (
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        ),
      },
      {
        path: '/register',
        element: (
          <PublicOnly>
            <RegisterPage />
          </PublicOnly>
        ),
      },
      {
        path: '/',
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          {
            path: 'pair',
            lazy: async () => ({ Component: (await import('@/pages/pair-page')).PairPage }),
          },
          {
            path: 'servers/:serverId',
            lazy: async () => ({ Component: (await import('@/pages/server-page')).ServerPage }),
          },
          {
            path: 'servers/:serverId/plugins/:pluginSlug',
            lazy: async () => ({
              Component: (await import('@/pages/plugin-editor-page')).PluginEditorPage,
            }),
          },
          {
            path: 'settings',
            lazy: async () => ({ Component: (await import('@/pages/settings-page')).SettingsPage }),
          },
          { path: '*', element: <NotFoundContent />, handle: NOT_FOUND_ROUTE },
        ],
      },
    ],
  },
];
